import { memo, useCallback, type ChangeEvent, type FC } from "react";
import { Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import { formatNumber } from "src/utils/numberFormat";
import CampaignStepCard from "../../../shared/CampaignStepCard";
import CampaignPaginationFooter from "../../../shared/CampaignPaginationFooter";
import WhatsAppTemplateCard from "../components/WhatsAppTemplateCard";
import type { WhatsAppTemplatesState } from "../hooks/UseWhatsAppTemplates";
import { TEMPLATE_STATUS_FILTERS, WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type { WhatsAppTemplate } from "../WhatsAppCampaignTypes";

const STEP_KEY = `${WHATSAPP_EDITOR_KEY}.chooseTemplate`;

interface ChooseWhatsAppTemplateStepProps {
  templatesState: WhatsAppTemplatesState;
  onUse: (_template: WhatsAppTemplate) => void;
  onEdit?: (_template: WhatsAppTemplate) => void;
  onCreate?: () => void;
}

const ChooseWhatsAppTemplateStep: FC<ChooseWhatsAppTemplateStepProps> = ({
  templatesState,
  onUse,
  onEdit,
  onCreate,
}) => {
  const { t, i18n } = useTranslation();
  const {
    templates,
    loadStatus,
    isConnected,
    errorMessage,
    hasTemplates,
    statusCounts,
    searchTerm,
    statusFilter,
    currentPage,
    totalPages,
    setSearchTerm,
    setStatusFilter,
    goToPreviousPage,
    goToNextPage,
    reload,
  } = templatesState;
  const isLoaded = loadStatus === "success";

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value),
    [setSearchTerm]
  );

  return (
    <CampaignStepCard
      wide
      title={t(`${STEP_KEY}.title`)}
      subtitle={t(`${STEP_KEY}.subtitle`)}
      headerAction={
        onCreate && (
          <button
            type="button"
            className="button button--primary campaign-flow__action wa-template-library__create"
            onClick={onCreate}
          >
            <Plus size={16} aria-hidden />
            {t(`${STEP_KEY}.createTemplate`)}
          </button>
        )
      }
      footer={
        isLoaded && templates.length > 0 && (
          <CampaignPaginationFooter
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={goToPreviousPage}
            onNext={goToNextPage}
          />
        )
      }
    >
      {loadStatus === "loading" && (
        <p role="status" className="wa-template-library__empty">
          {t(`${STEP_KEY}.loading`)}
        </p>
      )}

      {loadStatus === "error" && (
        <div role="alert" className="wa-template-library__empty">
          <p>
            {errorMessage ??
              t(isConnected ? `${STEP_KEY}.loadFailed` : `${STEP_KEY}.notConnected`)}
          </p>
          {isConnected && (
            <button
              type="button"
              className="button button--outline campaign-flow__action"
              onClick={reload}
            >
              {t(`${STEP_KEY}.retry`)}
            </button>
          )}
        </div>
      )}

      {isLoaded && (
        <>
          <div className="wa-template-library__toolbar">
            <StyledTextInput
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t(`${STEP_KEY}.searchPlaceholder`)}
              prefix={<Search size={16} aria-hidden />}
              className="campaign-flow__input wa-template-library__search"
              aria-label={t(`${STEP_KEY}.searchPlaceholder`)}
            />
            <div
              className="wa-template-library__tabs"
              role="group"
              aria-label={t(`${STEP_KEY}.filterLabel`)}
            >
              {TEMPLATE_STATUS_FILTERS.map(filter => (
                <button
                  key={filter}
                  type="button"
                  className={`wa-template-library__tab ${
                    filter === statusFilter ? "wa-template-library__tab--active" : ""
                  }`}
                  aria-pressed={filter === statusFilter}
                  onClick={() => setStatusFilter(filter)}
                >
                  {t(`${STEP_KEY}.filters.${filter}`)}
                  <span className="wa-template-library__tab-count">
                    {formatNumber(statusCounts[filter] ?? 0, {}, i18n.language)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {templates.length ? (
            <div className="wa-template-library__grid">
              {templates.map(template => (
                <WhatsAppTemplateCard
                  key={template.id}
                  template={template}
                  onUse={onUse}
                  onEdit={onEdit}
                />
              ))}
              {onCreate && (
                <button
                  type="button"
                  className="wa-template-library__create-card"
                  onClick={onCreate}
                >
                  <span className="wa-template-library__create-icon" aria-hidden>
                    <Plus size={18} />
                  </span>
                  <span className="wa-template-library__create-title">
                    {t(`${STEP_KEY}.createOwn.title`)}
                  </span>
                  <span className="wa-template-library__create-description">
                    {t(`${STEP_KEY}.createOwn.description`)}
                  </span>
                </button>
              )}
            </div>
          ) : (
            <p className="wa-template-library__empty">
              {t(hasTemplates ? `${STEP_KEY}.noResults` : `${STEP_KEY}.noTemplates`)}
            </p>
          )}
        </>
      )}
    </CampaignStepCard>
  );
};

export default memo(ChooseWhatsAppTemplateStep);
